import { useStacklineTheme } from '@stackline/ui';
import React, { useEffect, useCallback } from 'react';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import Box from '@mui/material/Box';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { AdjustmentPageIcon, DownloadPageIcon, ShortChecklist } from 'src/components/SvgIcons';
import {
  getAppName,
  getAppSubscriptionTier,
  shouldShowMicroTrends,
  shouldShowNewAtlas,
  shouldShowPresentations
} from 'src/utils/app';
import { useQueryParamValue, useAppSelector } from 'src/utils/Hooks';
import { useRuleData } from 'src/components/AddModals/RulesUpdateModal';
import { useQuery } from '@tanstack/react-query';
import {
  fetchScheduledActionGroupsByEntity,
  fetchScheduledActionGroups
} from 'src/components/BeaconV2Components/ExperimentalLayout/Advertising/Rules/RulesSummary/utils';
import colors from 'src/utils/colors';
import { MicroTrendsIcon, PresentationsPageIcon } from 'src/components/SvgIcons/SvgIcons';
import { NotificationBadge } from 'src/components/common/NotificationBadge';
import useNotificationsBadge from 'src/utils/Hooks/useNotificationsBadge';
import { DownloadsTab } from 'src/components/AtlasV2Components/AtlasV2Settings/AtlasV2DownloadsPageLayout';
import { ATLAS_SUBTABS, ATLAS_TABS } from 'src/components/AtlasV2Components/AtlasV2Sidebar/useAtlasV2Routes';
import CreditsQuickLink from './CreditsQuickLink';
import { AppName } from 'sl-api-connector';

/**
 * Below the sidebar, we have a quick link that redirect page to a certain page.
 */

const AppQuickLink = () => {
  const theme = useStacklineTheme();
  const { beaconNavigator } = useBeaconNavigator();
  const isAd = useQueryParamValue('tab') === 'advertising';
  const isBeacon = getAppName() === AppName.Beacon;

  const isGenerationOrProjects = window.location.pathname.includes('/generate') && isBeacon;

  const { rootData, setRootData } = useRuleData();
  const { mainEntity } = useAppSelector((state) => state.entityService);
  const hashId = useAppSelector((state) => state.user?.config?.vendor?.hashId);
  const shouldShowNotificationBadge = useNotificationsBadge(
    (state) =>
      state.notificationBadges.presentations || state.notificationBadges.export || state.notificationBadges.adjustments
  );

  const queryFn = useCallback(() => {
    if (mainEntity.type === 'adCampaign') {
      return fetchScheduledActionGroupsByEntity(mainEntity);
    } else {
      return fetchScheduledActionGroups(true);
    }
  }, [mainEntity]);

  const { data, isLoading } = useQuery({
    queryKey: ['scheduledActionGroups'],
    queryFn
  });

  useEffect(() => {
    setRootData(data ?? []);
  }, [data, isLoading, setRootData]);

  const filteredData = rootData?.filter((rule) => rule.scheduledActions[0].scheduledAction.status === 'enabled');

  const subscriptionTier = getAppSubscriptionTier();
  const isProUser = subscriptionTier === 'pro';

  return (
    <Flex
      gap={12}
      flexDirection="column"
      alignItems="flex-start"
      sx={{
        marginTop: '32px',
        padding: '12px 20px',
        borderRadius: '8px',
        backgroundColor: theme.colors.secondaryPorcelain,
        width: '185px',
        '& .MuiTypography-root': {
          lineHeight: '20px !important'
        }
      }}
    >
      {/* Download page link */}
      <SlButton
        variant="titleExtraSmall"
        textSx={{
          '&:hover': {
            textUnderlineOffset: '2px',
            textDecoration: 'underline',
            lineHeight: '20px'
          }
        }}
        buttonSx={{
          padding: 0,
          height: 'fit-content'
        }}
        onClick={() => {
          beaconNavigator.setPathname('/account/downloads').addSearchParams({ tab: DownloadsTab.ExportsDownload }).go();
        }}
        startIcon={<DownloadPageIcon style={{ width: '16px', height: '16px', marginRight: '8px' }} />}
      >
        Downloads
        {shouldShowNotificationBadge && <NotificationBadge variant="small" />}
      </SlButton>

      {shouldShowNewAtlas() && shouldShowPresentations() && (
        <SlButton
          variant="titleExtraSmall"
          textSx={{
            '&:hover': {
              textUnderlineOffset: '2px',
              textDecoration: 'underline',
              lineHeight: '20px'
            }
          }}
          buttonSx={{
            padding: 0,
            height: 'fit-content'
          }}
          onClick={() => {
            beaconNavigator.setPathname('/account/presentations');
            beaconNavigator.go();
          }}
          startIcon={<PresentationsPageIcon style={{ width: '16px', height: '16px', marginRight: '8px' }} />}
        >
          Presentations
        </SlButton>
      )}

      {/* Micro Trends */}
      {shouldShowMicroTrends() && (
        <SlButton
          variant="titleExtraSmall"
          textSx={{
            '&:hover': {
              textUnderlineOffset: '2px',
              textDecoration: 'underline',
              lineHeight: '20px'
            }
          }}
          buttonSx={{
            padding: 0,
            height: 'fit-content'
          }}
          onClick={() => {
            beaconNavigator.setPathname('/category/0');
            beaconNavigator.updateSearchParams({
              tab: ATLAS_TABS.SUMMARY_V2,
              subtab: ATLAS_SUBTABS.MICRO_TRENDS
            });
            beaconNavigator.go();
          }}
          startIcon={<MicroTrendsIcon style={{ width: '16px', height: '16px', marginRight: '8px' }} />}
        >
          Microtrends
        </SlButton>
      )}

      {/* Adjustments page link */}
      {!shouldShowNewAtlas() && isProUser && !isGenerationOrProjects && (
        <SlButton
          variant="titleExtraSmall"
          textSx={{
            '&:hover': {
              textUnderlineOffset: '2px',
              textDecoration: 'underline',
              lineHeight: '20px'
            }
          }}
          buttonSx={{
            padding: 0,
            height: 'fit-content'
          }}
          startIcon={<AdjustmentPageIcon style={{ width: '16px', height: '16px', marginRight: '8px' }} />}
          onClick={() => {
            beaconNavigator.setPathname('/account/adjustments');
            beaconNavigator.go();
          }}
        >
          Adjustments
        </SlButton>
      )}
      {isAd && (
        <Flex gap={8} alignItems="center">
          <SlButton
            variant="titleExtraSmall"
            buttonSx={{
              padding: 0,
              height: 'fit-content',
              minWidth: 'unset'
            }}
            textSx={{
              '&:hover': {
                textUnderlineOffset: '2px',
                textDecoration: 'underline'
              }
            }}
            onClick={() => {
              if (mainEntity.type === 'product') {
                beaconNavigator.setPathname(`client/${hashId}`);
              }

              beaconNavigator.addSearchParams({
                tab: 'advertising',
                subtab: 'rulesSummary'
              });
              beaconNavigator.go();
            }}
            startIcon={<ShortChecklist style={{ width: '16px', height: '16px', marginRight: '8px' }} />}
          >
            Rules
          </SlButton>
          {filteredData?.length > 0 && mainEntity.type !== 'product' && (
            <Box
              sx={{
                padding: '4px 8px',
                backgroundColor: colors.pastelGreen,
                color: colors.darkGreen,
                borderRadius: '4px',
                fontSize: '12px'
              }}
            >
              {filteredData?.length} active
            </Box>
          )}
        </Flex>
      )}

      {/* Credits section - show for Atlas or Beacon Pro users */}
      {(getAppName() === AppName.Atlas || (isBeacon && isProUser)) && <CreditsQuickLink />}
    </Flex>
  );
};

export default AppQuickLink;
